import * as React from "react"
import { useState } from "react"
import Chip, { type ChipVariantKey, chipVariants } from "@imminently/decisively-ui/Chip"
import Icon from "@imminently/decisively-ui/Icon"
import Select from "@imminently/decisively-ui/Select"
import Switch from "@imminently/decisively-ui/Switch"

import {
  ComponentPage,
  ComponentSection,
  ComponentPreview,
  ControlPanel,
  ControlRow,
} from "../components/ComponentPage"

const variantKeys = Object.keys(chipVariants) as ChipVariantKey[]
const sizeKeys = ["sm", "md", "lg"] as const

export function ChipPage() {
  const [variant, setVariant] = useState<ChipVariantKey>("secondary")
  const [size, setSize] = useState<(typeof sizeKeys)[number]>("md")
  const [withIcon, setWithIcon] = useState(false)
  const [outlined, setOutlined] = useState(false)

  return (
    <ComponentPage
      title="Chip"
      description="A small labelled pill used for statuses, tags, and warnings."
    >
      {/* Interactive Playground */}
      <ComponentSection
        title="Playground"
        description="Experiment with different chip configurations"
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <ComponentPreview>
            <Chip
              variant={variant}
              size={size}
              outlined={outlined}
              icon={withIcon ? <Icon name="info" /> : undefined}
              label="Chip label"
            />
          </ComponentPreview>

          <ControlPanel>
            <ControlRow label="Variant">
              <Select
                value={variant}
                onValueChange={(v) => setVariant((v ?? "secondary") as ChipVariantKey)}
                options={variantKeys.map((key) => ({ value: key, label: key }))}
              />
            </ControlRow>
            <ControlRow label="Size">
              <Select
                value={size}
                onValueChange={(v) => setSize((v ?? "md") as (typeof sizeKeys)[number])}
                options={sizeKeys.map((key) => ({ value: key, label: key }))}
              />
            </ControlRow>
            <ControlRow label="With Icon">
              <Switch checked={withIcon} onCheckedChange={setWithIcon} />
            </ControlRow>
            <ControlRow label="Outlined">
              <Switch checked={outlined} onCheckedChange={setOutlined} />
            </ControlRow>
          </ControlPanel>
        </div>
      </ComponentSection>

      {/* All Variants */}
      <ComponentSection title="Variants" description="All colour variants">
        <ComponentPreview className="flex-wrap gap-4">
          {variantKeys.map((key) => (
            <Chip key={key} variant={key} label={key} />
          ))}
        </ComponentPreview>
      </ComponentSection>

      {/* Sizes */}
      <ComponentSection title="Sizes" description="Available chip sizes">
        <ComponentPreview className="flex-wrap gap-4 items-center">
          {sizeKeys.map((key) => (
            <Chip key={key} size={key} label={key} />
          ))}
        </ComponentPreview>
      </ComponentSection>

      {/* Outlined */}
      <ComponentSection title="Outlined" description="Adds a coloured border on top of the solid variant background">
        <ComponentPreview className="flex-wrap gap-4">
          {variantKeys.map((key) => (
            <Chip key={key} variant={key} outlined label={key} />
          ))}
        </ComponentPreview>
      </ComponentSection>

      {/* With Icons */}
      <ComponentSection title="With Icons" description="Chips with a leading icon">
        <ComponentPreview className="flex-wrap gap-4">
          {variantKeys.map((key) => (
            <Chip key={key} variant={key} icon={<Icon name="info" />} label={key} />
          ))}
        </ComponentPreview>
      </ComponentSection>

      {/* Custom children */}
      <ComponentSection title="Custom content" description="Chip.Icon and Chip.Label composed directly, or fully custom children">
        <ComponentPreview className="flex-wrap gap-4">
          <Chip variant="success">
            <Chip.Icon><Icon name="check" /></Chip.Icon>
            <Chip.Label>Composed</Chip.Label>
          </Chip>
          <Chip variant="info" label={<span className="capitalize">Capitalized label</span>} />
        </ComponentPreview>
      </ComponentSection>

      {/* Variant × Size Matrix */}
      <ComponentSection title="Variant × Size Matrix" description="All combinations of variants and sizes">
        <div className="overflow-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="p-3 text-left text-sm font-medium text-muted-foreground"></th>
                {sizeKeys.map((size) => (
                  <th key={size} className="p-3 text-center text-sm font-medium text-muted-foreground capitalize">
                    {size}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {variantKeys.map((key) => (
                <tr key={key} className="border-t border-border">
                  <td className="p-3 text-sm font-medium capitalize">{key}</td>
                  {sizeKeys.map((size) => (
                    <td key={size} className="p-3 text-center">
                      <Chip variant={key} size={size} label={key} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ComponentSection>
    </ComponentPage>
  )
}
